/* @flow */

import classNames from 'classnames';
import { compose } from 'ramda';
import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { isString } from '@e180/util/lib/type';

import styles from './Avatar.style';

import { large, xlarge, xxlarge } from './setting/avatarVariants';
import { getAvatarSizeFromVariant } from './util/avatarUtils';
import { getPictureNormalAndRetinaSrc } from '@e180/util/lib/image';

import type { Picture } from '@e180/domain/lib/base/type';
import type { AvatarProps } from './type/avatarTypes';

type Props = AvatarProps & {
  src: Array<string | Picture>,
};

const useStyles = createUseStyles(styles);

// $FlowIssue: Flow bug?
const GroupAvatar = ({ layoutClass, variant, alt, src }: Props): React.Node => {
  const isLarge = variant === large;
  const isXLarge = variant === xlarge;
  const isXXLarge = variant === xxlarge;
  const isMedium = !(isLarge || isXLarge || isXXLarge);
  const size = variant && getAvatarSizeFromVariant(variant);
  const [leftSrc, topSrc, bottomSrc] = src.map((s) =>
    isString(s) ? [s] : size ? getPictureNormalAndRetinaSrc(s, size) : [],
  );

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles({
    leftSrc,
    topSrc,
    bottomSrc,
  });

  const rootClasses = classNames(
    classes.root,
    classes['root-isGroup'],
    layoutClass,
    {
      [classes['root-medium']]: isMedium,
      [classes['root-large']]: isLarge,
      [classes['root-xlarge']]: isXLarge,
      [classes['root-xxlarge']]: isXXLarge,
    },
  );

  const leftClasses = classNames(classes.left, {
    [classes['left-medium']]: isMedium,
    [classes['left-large']]: isLarge,
    [classes['left-xlarge']]: isXLarge,
    [classes['left-xxlarge']]: isXXLarge,
  });

  const rightClasses = classNames(classes.right, {
    [classes['right-medium']]: isMedium,
    [classes['right-large']]: isLarge,
    [classes['right-xlarge']]: isXLarge,
    [classes['right-xxlarge']]: isXXLarge,
  });

  const topClasses = classNames(classes.top, {
    [classes['top-medium']]: isMedium,
    [classes['top-large']]: isLarge,
    [classes['top-xlarge']]: isXLarge,
    [classes['top-xxlarge']]: isXXLarge,
  });

  const bottomClasses = classNames(classes.bottom, {
    [classes['bottom-medium']]: isMedium,
    [classes['bottom-large']]: isLarge,
    [classes['bottom-xlarge']]: isXLarge,
    [classes['bottom-xxlarge']]: isXXLarge,
  });

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  return (
    <div className={rootClasses} aria-label={alt}>
      <div className={leftClasses} />
      <div className={rightClasses}>
        <div className={topClasses} />
        <div className={bottomClasses} />
      </div>
    </div>
  );
};

// Make variants available
export * from './setting/avatarVariants';

export default compose(React.memo)(GroupAvatar);
